import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Download, Code2 } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should a web developer invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A web developer invoice should include your name or company, the client name and project name, a unique invoice number and date, payment due date, a description of the work completed (development phase, features built, or hours worked), your rate and total, any expenses like hosting or third-party tools, applicable tax, and your payment details.',
      },
    },
    {
      '@type': 'Question',
      name: 'Should web developers bill hourly or per project?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Both are valid. Hourly billing works well for ongoing maintenance, bug fixes, and support retainers. Fixed project fees suit clearly scoped work like building a website or app feature. Many developers charge a fixed fee for the initial build and then hourly for changes and maintenance. Either way, break the invoice into clear line items so the client understands what they are paying for.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I invoice for a web development milestone?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'For milestone billing, create a separate invoice for each milestone reached — for example, "Phase 1: Design & Architecture" or "Phase 2: Frontend Development." Reference the project agreement and milestone number in the invoice description. This creates a clear paper trail and aligns with typical client payment approval processes.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I include hosting and domain costs on a developer invoice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Add third-party expenses like hosting, domain registration, SSL certificates, or software licences as separate line items on your invoice. Mark them clearly as "reimbursable expenses" or "third-party costs" and attach receipts if the client requests them. Some developers add a small markup on these costs; if you do, disclose it.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this web developer invoice template free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic provides a free web developer invoice template. Fill in your project details online and download a professional PDF instantly. Free for up to 5 invoices per month with no sign-up required.',
      },
    },
  ],
};

export default function WebDeveloperInvoiceTemplatePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Web Developer Invoice Template: Free PDF for Developers | Invilic"
        description="Free web developer invoice template. Bill for development projects, hourly work, milestones, and hosting expenses. Download a professional PDF. No sign-up needed."
        canonical="https://www.invilic.com/web-developer-invoice-template"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            Free Template
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Web Developer Invoice Template
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            A professional invoice template for web developers and software engineers. Bill for projects, hourly work, milestones, and reimbursable expenses. Download a clean PDF in seconds — no account needed.
          </p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-sky-600/20 transition-all duration-200 text-lg"
          >
            Use Free Template <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Built for Every Type of Developer
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Freelance web developers', desc: 'Bill clients for website builds, landing pages, and CMS customisation by hour or fixed fee.' },
              { title: 'Full-stack developers', desc: 'Invoice for frontend, backend, and API development with milestone-based billing.' },
              { title: 'WordPress & Shopify developers', desc: 'Bill for theme customisation, plugin development, and e-commerce setup projects.' },
              { title: 'Maintenance & support developers', desc: 'Issue monthly retainer invoices for bug fixes, updates, and ongoing technical support.' },
            ].map(({ title, desc }) => (
              <div key={title} className="flex gap-4 p-5 rounded-xl border border-gray-100 dark:border-gray-800">
                <div className="w-10 h-10 rounded-xl bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center flex-shrink-0">
                  <Code2 className="w-5 h-5 text-sky-600 dark:text-sky-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{title}</div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's in the Web Developer Invoice Template
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Developer / company name and logo',
              'Client name and project name',
              'Invoice number and date',
              'Payment due date',
              'Development work per line item',
              'Hours worked or milestone fee',
              'Hosting, domain and third-party costs',
              'Rush or out-of-scope charges',
              'Subtotal, tax and discount',
              'Total amount due',
              'Bank details or payment link',
              'Notes and project reference',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-sky-100 dark:border-sky-800/30 bg-sky-50 dark:bg-sky-900/10">
                <CheckCircle className="w-4 h-4 text-sky-600 dark:text-sky-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            How to Create Your Developer Invoice
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { n: '1', title: 'Open the template', desc: 'Click "Use Free Template" above. No software to install, no account needed.' },
              { n: '2', title: 'Add your work', desc: 'Enter project details, hours or milestone fee, and any reimbursable expenses.' },
              { n: '3', title: 'Download PDF', desc: 'Download a professional PDF invoice ready to send to your client.' },
            ].map(({ n, title, desc }) => (
              <div key={n} className="text-center p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                <div className="w-10 h-10 rounded-full bg-sky-600 text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">
                  {n}
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Web Developer Invoice Template: FAQs
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your Developer Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF. No account required. Ready in 2 minutes.</p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-sky-600/20 transition-all duration-200 text-lg"
          >
            <Download className="w-5 h-5" /> Get Free Web Developer Invoice Template
          </Link>
        </section>
      </main>
    </div>
  );
}
